import React, { useEffect, useRef } from 'react';

interface AntigravityProps {
  count?: number;
  magnetRadius?: number;
  particleSize?: number;
  lerpSpeed?: number;
  color?: string;
  fieldStrength?: number;
}

const Antigravity: React.FC<AntigravityProps> = ({
  count = 200,
  magnetRadius = 200,
  particleSize = 2.5,
  lerpSpeed = 0.1,
  color = '#7036e3',
  fieldStrength = 60,
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const particlesRef = useRef<Array<{
    x: number;
    y: number;
    vx: number;
    vy: number;
    baseX: number;
    baseY: number;
  }>>([]);
  
  const mouseRef = useRef({
    x: window.innerWidth / 2,
    y: window.innerHeight / 2,
  });

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) {
      // Silently fail instead of logging error
      return;
    }

    // Set canvas to full window size
    const resizeCanvas = () => {
      try {
        canvas.width = window.innerWidth;
        canvas.height = window.innerHeight;
      } catch (err) {
        // Silently handle resize errors
      }
    };

    resizeCanvas();
    window.addEventListener('resize', resizeCanvas);

    const ctx = canvas.getContext('2d');
    if (!ctx) {
      // Silently fail instead of logging error
      return;
    }

    // Initialize particles
    particlesRef.current = Array.from({ length: count }, () => {
      const x = Math.random() * canvas.width;
      const y = Math.random() * canvas.height;
      return { x, y, vx: 0, vy: 0, baseX: x, baseY: y };
    });

    // Track mouse movement - use both window and document for reliability
    const handleMouseMove = (e: MouseEvent) => {
      mouseRef.current.x = e.clientX;
      mouseRef.current.y = e.clientY;
    };

    window.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mousemove', handleMouseMove);

    // Animation loop
    let animationId: number;
    const animate = () => {
      // Clear canvas
      ctx.fillStyle = 'rgba(249, 250, 251, 1)';
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      const mouseX = mouseRef.current.x;
      const mouseY = mouseRef.current.y;

      // Update particles
      particlesRef.current.forEach((particle) => {
        const dx = mouseX - particle.x;
        const dy = mouseY - particle.y;
        const distance = Math.sqrt(dx * dx + dy * dy);

        // Repulsion zone
        const repulsionRadius = magnetRadius;
        if (distance < repulsionRadius && distance > 0.1) {
          // Calculate repulsion - much stronger
          const forceStrength = ((repulsionRadius - distance) / repulsionRadius) * fieldStrength * 2;
          const angle = Math.atan2(dy, dx);

          // Push particles away with stronger force
          particle.vx -= Math.cos(angle) * forceStrength;
          particle.vy -= Math.sin(angle) * forceStrength;
        }

        // Return to base position - always pull particles back gently
        const returnX = particle.baseX - particle.x;
        const returnY = particle.baseY - particle.y;
        const returnDist = Math.sqrt(returnX * returnX + returnY * returnY);

        if (returnDist > 0.1) {
          // Apply spring force to keep particles anchored
          particle.vx += returnX * lerpSpeed * 0.12;
          particle.vy += returnY * lerpSpeed * 0.12;
        } else {
          // Snap to exact position when very close
          particle.x = particle.baseX;
          particle.y = particle.baseY;
        }

        // Damping - stop particles from wandering
        particle.vx *= 0.82;
        particle.vy *= 0.82;

        // Kill very small velocities to prevent drifting
        if (Math.abs(particle.vx) < 0.02) particle.vx = 0;
        if (Math.abs(particle.vy) < 0.02) particle.vy = 0;

        // Limit velocity
        const maxVel = 10;
        const vel = Math.sqrt(particle.vx ** 2 + particle.vy ** 2);
        if (vel > maxVel) {
          particle.vx = (particle.vx / vel) * maxVel;
          particle.vy = (particle.vy / vel) * maxVel;
        }

        // Update position
        particle.x += particle.vx;
        particle.y += particle.vy;

        // Wrap around
        if (particle.x < 0) particle.x = canvas.width;
        if (particle.x > canvas.width) particle.x = 0;
        if (particle.y < 0) particle.y = canvas.height;
        if (particle.y > canvas.height) particle.y = 0;

        // Draw
        ctx.fillStyle = color;
        ctx.beginPath();
        ctx.arc(particle.x, particle.y, particleSize, 0, Math.PI * 2);
        ctx.fill();
      });

      animationId = requestAnimationFrame(animate);
    };

    animate();

    return () => {
      window.removeEventListener('resize', resizeCanvas);
      window.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mousemove', handleMouseMove);
      cancelAnimationFrame(animationId);
    };
  }, [count, magnetRadius, fieldStrength, lerpSpeed, color, particleSize]);

  return (
    <canvas
      ref={canvasRef}
      style={{
        display: 'block',
        position: 'fixed',
        top: 0,
        left: 0,
        zIndex: 0,
      }}
    />
  );
};

export default Antigravity;
